import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import useMediaQuery from "@mui/material/useMediaQuery";
import { GridPreferencePanelsValue, GridRowId, GridRowSelectionModel, useGridApiRef } from "@mui/x-data-grid-pro";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { Suspense, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuditAccessGate } from "./components/common/AuditAccessGate";
import { AuditGrid } from "./components/grid/AuditGrid";
import { rowStartTrip, rowStopTrip, rowTrips } from "./components/grid/rowValues";
import { useAuditColumns } from "./components/grid/useAuditColumns";
import { FilterBar } from "./components/header/FilterBar";
import { GroupFilterButton } from "./components/header/GroupFilterButton";
import { LoadProgress } from "./components/header/LoadProgress";
import { PageHeaderBar } from "./components/header/PageHeaderBar";
import { StatusMeta } from "./components/header/StatusMeta";
import { ViewSwitch } from "./components/header/ViewSwitch";
import { DayCardList } from "./components/mobile/DayCardList";
import { PeriodSummaryCard } from "./components/summary/PeriodSummaryCard";
import { SummaryToggle } from "./components/summary/SummaryToggle";
import { AuditSource, useAuditData } from "./hooks/useAuditData";
import { useAuditDevices } from "./hooks/useAuditDevices";
import { useAuditExport } from "./hooks/useAuditExport";
import { useAuditFilters } from "./hooks/useAuditFilters";
import { useAuditGridState } from "./hooks/useAuditGridState";
import { useAuditRows } from "./hooks/useAuditRows";
import { useAuditSettings } from "./hooks/useAuditSettings";
import { useAuditTimeZone } from "./hooks/useAuditTimeZone";
import { useCanEditTrips } from "./hooks/useCanEditTrips";
import { useDriverLabels } from "./hooks/useDriverLabels";
import { useExportWhenAddressesReady } from "./hooks/useExportWhenAddressesReady";
import { loadStepForVehicles, useLoadedVehicles } from "./hooks/useLoadedVehicles";
import { AuditRow } from "./models/AuditRow";
import { AuditTrip, displayReason } from "./models/AuditTrip";
import { AuditView } from "./models/AuditView";
import { auditTheme } from "./theme/auditTheme";
import { AUDIT_TOKENS } from "./theme/auditTokens";
import { auditEmptyReason } from "./utils/auditEmptyReason";
import { auditLoadErrorBody } from "./utils/auditLoadErrorBody";
import { buildPeriodSummary, containsFullCalendarMonth } from "./utils/periodSummary";
import { formatDayInZone } from "./utils/timeZone";
import { assignmentBlocker, toTripRef } from "./utils/tripAssignment";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { useTripAddresses } from "@/hooks/addresses/useTripAddresses";
import { useGroups } from "@/hooks/groups/useGroups";
import { useHasPermission } from "@/hooks/groups/useHasPermission";
import { useDaysToEdit } from "@/hooks/logbook/useDaysToEdit";
import { TripChanges, useSaveChanges } from "@/hooks/logbook/useSaveChanges";
import { AUDIT_TRIPS_QUERY_KEY } from "@/hooks/trips/useTripsForDevices";
import { useUserStateRows } from "@/hooks/userState/useUserStateRows";
import { demystifyError } from "@/utilities/error/demystifyError";
import { ManualRecordsCapError } from "@/utilities/error/ManualRecordsCapError";
import { ResultsCapError } from "@/utilities/error/ResultsCapError";
import { formatNumber } from "@/utilities/formatNumber";
import { buildGroupIndex } from "@/utilities/groupIndex";
import { mapLazy } from "@/utilities/mapLazy";
import { isAdministrator } from "@/utilities/user/isAdministrator";

/** Geotab security identifier of "View Add-In data". */
const VIEW_ADD_IN_DATA = "ViewAddInData";

const TripMapDrawer = mapLazy("TripMapDrawer", import("./components/map/TripMapDrawer"));
const SettingsPopover = mapLazy("SettingsPopover", import("./components/settings/SettingsPopover"));
const TripDetailDialog = mapLazy("TripDetailDialog", import("./components/detail/TripDetailDialog"));
const HelpDrawer = mapLazy("HelpDrawer", import("./components/help/HelpDrawer"));
const AssignBar = mapLazy("AssignBar", import("./components/edit/AssignBar"));

interface MapSelection {
  title: string;
  trips: AuditTrip[];
}

/**
 * The audit needs the AddInData API (bookings, tax classes, grid layout). Without that clearance
 * the page explains what is missing instead of loading: the Geotab client batches concurrent
 * reads into one ExecuteMultiCall, so a single forbidden call would also empty the vehicle list.
 */
export default function LogbookAuditPage() {
  const { hasPermission, isLoading, isResolved, isError, isFetching, retry } = useHasPermission(VIEW_ADD_IN_DATA);
  // The remembered date range and settings decide what is queried, so nothing loads before they are known.
  const { isReady: userStateReady } = useUserStateRows();
  if (!userStateReady) return null;
  return (
    <AuditAccessGate
      clearance={VIEW_ADD_IN_DATA}
      hasPermission={hasPermission}
      isLoading={isLoading}
      isResolved={isResolved}
      isError={isError}
      isRetrying={isFetching}
      onRetry={retry}
    >
      <LogbookAuditContent />
    </AuditAccessGate>
  );
}

function LogbookAuditContent() {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { me, activeSessionId } = useGeotabClient();
  const queryClient = useQueryClient();
  const { groups: globalGroups, groupFilterCondition } = useAddInState();
  const isMobile = useMediaQuery("(max-width:820px)");
  const apiRef = useGridApiRef();

  const timeZone = useAuditTimeZone();
  const filters = useAuditFilters(timeZone);
  const settings = useAuditSettings();
  const [view, setView] = useState<AuditView>("digital");
  const [helpOpen, setHelpOpen] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
  const [mapSelection, setMapSelection] = useState<MapSelection | null>(null);
  const [detailTrip, setDetailTrip] = useState<AuditTrip | null>(null);
  const [expandedDayIds, setExpandedDayIds] = useState<Set<GridRowId>>(() => new Set());
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const { data: editDays = 7 } = useDaysToEdit();
  const save = useSaveChanges();

  const { data: allGroups } = useGroups();
  const groupIndex = useMemo(() => buildGroupIndex(allGroups), [allGroups]);
  const canEdit = useCanEditTrips(groupIndex);
  const isAdmin = isAdministrator(me);
  // Administrators switch vehicle types off for the auditors; they themselves see every vehicle.
  const hiddenTypeIds = useMemo(
    () => new Set(isAdmin ? [] : settings.auditorGroups.flatMap(entry => (entry.enabled ? [] : [entry.id]))),
    [isAdmin, settings.auditorGroups],
  );
  const {
    devices,
    isLoading: devicesLoading,
    isFetching: devicesFetching,
    isError: devicesFailed,
    error: devicesError,
  } = useAuditDevices(
    filters.groups,
    filters.devices,
    groupIndex,
    settings.includeArchived,
    hiddenTypeIds,
  );
  const vehicles = useLoadedVehicles(devices, loadStepForVehicles(filters.devices.length));
  const data = useAuditData({
    devices: vehicles.loadedDevices,
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    taxClasses: settings.taxClasses,
    groupIndex,
    timeZone,
  });
  const { rows, viewTrips, summaryTrips, counts } = useAuditRows({
    trips: data.trips,
    view,
    reasons: filters.reasons,
    timeZone,
  });
  const periodSummary = useMemo(
    () =>
      buildPeriodSummary({
        trips: summaryTrips,
        vehicleDays: data.vehicleDays,
        dayConsumption: data.dayConsumption,
        refuels: data.periodRefuels,
        detected: data.periodDetected,
        devices: data.auditDevices,
      }),
    [summaryTrips, data.vehicleDays, data.dayConsumption, data.periodRefuels, data.periodDetected, data.auditDevices],
  );
  const splitAvailable = containsFullCalendarMonth(filters.fromDay, filters.toDay);
  const gridState = useAuditGridState(view);
  // The grid remounts per view and once the stored layout is in; column filters start empty then.
  const gridKey = `${view}-${gridState.ready ? "stored" : "default"}`;
  const [filterCount, setFilterCount] = useState({ gridKey, count: 0 });
  const activeFilterCount = filterCount.gridKey === gridKey ? filterCount.count : 0;
  const driverIds = useMemo(() => data.trips.flatMap(trip => (trip.driverId ? [trip.driverId] : [])), [data.trips]);
  // Administrators and editors see driver names, the customs auditor only the user id.
  const { driverLabel } = useDriverLabels(driverIds, canEdit || isAdmin);

  // Selection only on the grid (the phone cards have none) and in the digital view: analog
  // entries are never assignable, so their view needs no checkboxes.
  const editable = canEdit && view === "digital" && !isMobile;
  const selectedTrips = useMemo(() => {
    if (!editable || selectionModel.length === 0) return [];
    const ids = new Set(selectionModel);
    return rows.flatMap(row => (row.rowType === "trip" && ids.has(row.id) ? [row.trip] : []));
  }, [editable, selectionModel, rows]);
  // Trips of a vehicle whose check is not "Okay" stay selectable: the bar then explains why
  // saving is locked instead of silently offering no checkbox.
  const isRowSelectable = useCallback(
    (row: AuditRow) => {
      const now = new Date();
      return rowTrips(row).some(trip => {
        const blocker = assignmentBlocker(trip, editDays, now);
        return blocker === undefined || blocker === "vehicleCheck";
      });
    },
    [editDays],
  );
  const changeView = (next: AuditView) => {
    setSelectionModel([]);
    setView(next);
  };
  const handleAssign = (changes: TripChanges) => {
    const now = new Date();
    const trips = selectedTrips.filter(trip => assignmentBlocker(trip, editDays, now) === undefined);
    if (trips.length !== selectedTrips.length) return;
    save.mutate(
      { trips: trips.map(toTripRef), ...changes },
      {
        onSuccess: () => {
          for (const deviceId of new Set(trips.map(trip => trip.device.id))) {
            queryClient.invalidateQueries({ queryKey: [AUDIT_TRIPS_QUERY_KEY, activeSessionId, deviceId] });
          }
          setSelectionModel([]);
          enqueueSnackbar(t("logbookAudit.assign.saved"), { variant: "success" });
        },
        onError: error => {
          console.error(error);
          const message = error instanceof ResultsCapError ? error.i18nMessage(t) : t("logbookAudit.assign.failed");
          enqueueSnackbar(message, { variant: "error" });
        },
      },
    );
  };

  const [exportRequested, setExportRequested] = useState(false);
  // Addresses come from Geotab's positions, so only digital entries have them. They are resolved
  // for the day rows first; trip rows get theirs once a day is expanded. An export needs the
  // address of every exported row, so it first widens the set to all rows and waits (see below).
  const addressTrips = useMemo(() => {
    const trips = new Map<string, AuditTrip>();
    if (exportRequested) {
      for (const row of rows) {
        for (const trip of [rowStartTrip(row), rowStopTrip(row)]) {
          if (trip.recordType === "digital") trips.set(trip.id, trip);
        }
      }
      return [...trips.values()];
    }
    for (const row of rows) {
      if (row.rowType !== "day" || row.recordTypes.includes("analog")) continue;
      trips.set(row.firstTrip.id, row.firstTrip);
      trips.set(row.lastTrip.id, row.lastTrip);
      if (expandedDayIds.has(row.id)) for (const trip of row.trips) trips.set(trip.id, trip);
    }
    return [...trips.values()];
  }, [exportRequested, rows, expandedDayIds]);
  // Looked up once the trips are in: the lookback for a first trip reuses the loaded lead week.
  const {
    getStartAddress,
    getStopAddress,
    isReady: addressesReady,
    isError: addressesFailed,
  } = useTripAddresses(addressTrips, data.geotabTrips, { enabled: !data.isLoadingTrips });

  const showMap = useCallback(
    (row: AuditRow) => {
      const trips = rowTrips(row);
      const title =
        row.rowType === "day"
          ? `${row.device.name} · ${formatDayInZone(row.date, timeZone)}`
          : `${row.device.name} · ${formatDayInZone(row.trip.start, timeZone)}`;
      setMapSelection({ title, trips });
    },
    [timeZone],
  );

  const showReasonMap = useCallback(
    (row: AuditRow, reasonLabel: string) => {
      const trips = rowTrips(row).filter(trip => displayReason(trip) === reasonLabel);
      if (trips.length === 0) return;
      const day = row.rowType === "day" ? row.date : row.trip.start;
      setMapSelection({ title: `${row.device.name} · ${formatDayInZone(day, timeZone)} · ${reasonLabel}`, trips });
    },
    [timeZone],
  );

  const columns = useAuditColumns({
    view,
    distanceDecimals: settings.distanceDecimals,
    driverLabel,
    getStartAddress,
    getStopAddress,
    onShowMap: showMap,
    onShowReasonMap: showReasonMap,
  });

  const failedSources: (AuditSource | "devices")[] = [...(devicesFailed ? (["devices"] as const) : []), ...data.failedSources];
  const loadFailed = failedSources.length > 0;
  const emptyReason = auditEmptyReason({
    loadFailed,
    devicesLoading,
    deviceCount: devices.length,
    tripsLoading: data.isFetching,
    tripCount: data.trips.length,
    rowCount: rows.length,
  });
  // Without the vehicle list the header has no counts to show; 0 would claim an empty fleet.
  const vehicleListReady = !devicesLoading && !devicesFailed;
  // Same for the trip counts: a failed or still empty-handed load must show "unknown", never 0.
  const loadingWithoutTrips = (devicesLoading || data.isFetching) && counts.total === 0;
  const tripCountsKnown = !loadFailed && !loadingWithoutTrips;

  const { exportAudit, exportSummary, exporting } = useAuditExport({
    apiRef,
    columns,
    rows,
    view,
    filters,
    timeZone,
    distanceDecimals: settings.distanceDecimals,
    driverLabel,
    getStartAddress,
    getStopAddress,
    summary: periodSummary,
    includeSplit: splitAvailable,
    vehicles: { shown: vehicles.shown, total: vehicles.total },
  });

  // The Excel export carries the addresses of all rows, not only the visible ones: the request
  // widens the geocoding to every row, and the file is built once all of them are in.
  useExportWhenAddressesReady({
    requested: exportRequested,
    setRequested: setExportRequested,
    // Trips and details (bookings, consumption): the exported rows are final only when both are in.
    loading: data.isFetching,
    addressesReady,
    addressesFailed,
    run: exportAudit,
  });

  const toggleColumnChooser = () => {
    const panel = apiRef.current.state.preferencePanel;
    if (panel.open && panel.openedPanelValue === GridPreferencePanelsValue.columns) apiRef.current.hidePreferences();
    else apiRef.current.showPreferences(GridPreferencePanelsValue.columns);
  };

  // The grid keeps its bar until every figure of the rows is in, not only the trips.
  const loading = devicesLoading || data.isFetching;
  const groupFilterActive =
    (globalGroups?.length ?? 0) > 0 || !!groupFilterCondition || filters.groups.length > 0 || hiddenTypeIds.size > 0;
  const number = (value: number) => formatNumber(value, i18n.language);
  const loadMoreProps = {
    shown: vehicles.shown,
    total: vehicles.total,
    canLoadMore: vehicles.canLoadMore,
    nextStep: vehicles.nextStep,
    onLoadMore: vehicles.loadMore,
    // A failed or still empty-handed load shows "—", never "0 of 0".
    entries: tripCountsKnown ? t("logbookAudit.filters.count", { shown: number(viewTrips.length), total: number(counts.total) }) : "—",
    status:
      data.isLoadingTrips || data.isLoadingDetails ? (
        <LoadProgress loaded={data.loadedCount} total={data.totalCount} loadingTrips={data.isLoadingTrips} loadingDetails={data.isLoadingDetails} />
      ) : (
        <StatusMeta
          updatedAt={data.dataUpdatedAt ? new Date(data.dataUpdatedAt) : undefined}
          timeZone={timeZone}
          vehicleCount={vehicleListReady ? devices.length : undefined}
          tripCount={tripCountsKnown ? counts.total : undefined}
          groupFilterActive={groupFilterActive}
          refreshing={devicesFetching || data.isFetching}
        />
      ),
  };

  return (
    <ThemeProvider theme={auditTheme}>
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          p: { xs: "10px 10px 16px", sm: "10px 14px" },
          bgcolor: AUDIT_TOKENS.bg,
          color: AUDIT_TOKENS.text,
          fontFamily: AUDIT_TOKENS.fontFamily,
          overflow: { xs: "auto", md: "hidden" },
          "& *, & *::before, & *::after": { boxSizing: "border-box" },
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <PageHeaderBar
            groupFilter={<GroupFilterButton value={filters.groups} onChange={filters.setGroups} />}
            helpOpen={helpOpen}
            onToggleHelp={() => setHelpOpen(open => !open)}
            onRefresh={data.refresh}
            refreshing={devicesFetching || data.isFetching}
            onExport={() => setExportRequested(true)}
            exportDisabled={rows.length === 0 || exporting || exportRequested || data.isFetching || loadFailed}
            exportPreparing={exportRequested}
            columns={
              isMobile
                ? undefined
                : {
                    onOpenColumns: toggleColumnChooser,
                    headerFiltersShown: settings.showHeaderFilters,
                    onToggleHeaderFilters: () => settings.setShowHeaderFilters(!settings.showHeaderFilters),
                    activeFilterCount,
                  }
            }
            onOpenSettings={setSettingsAnchor}
          >
            <ViewSwitch view={view} onChange={changeView} counts={tripCountsKnown ? counts : undefined} />
          </PageHeaderBar>
          <FilterBar
            filters={filters}
            includeArchived={settings.includeArchived}
            trailing={<SummaryToggle open={settings.summaryOpen} onToggle={settings.setSummaryOpen} />}
          />
          {loadFailed && (
            <Alert severity="error" sx={{ mb: "10px" }}>
              <AlertTitle>{t("logbookAudit.loadError.title")}</AlertTitle>
              {auditLoadErrorBody(t, devicesError, failedSources)}
              {data.bookingsError instanceof ManualRecordsCapError && (
                <Box sx={{ mt: "4px" }}>{demystifyError(t, data.bookingsError)}</Box>
              )}
            </Alert>
          )}
          <PeriodSummaryCard
            summary={periodSummary}
            splitAvailable={splitAvailable}
            loading={loading}
            unavailable={loadFailed}
            vehicles={{ shown: vehicles.shown, total: vehicles.total }}
            open={settings.summaryOpen}
            onClose={() => settings.setSummaryOpen(false)}
            isMobile={isMobile}
            onExport={exportSummary}
            exportDisabled={periodSummary.vehicles.length === 0 || exporting || loadFailed}
          />
        </Box>
        <Box sx={{ flex: 1, minHeight: isMobile ? "auto" : 220, display: "flex", flexDirection: "column" }}>
          {isMobile ? (
            <DayCardList
              rows={rows}
              view={view}
              distanceDecimals={settings.distanceDecimals}
              driverLabel={driverLabel}
              onShowMap={showMap}
              loadMore={loadMoreProps}
              emptyReason={emptyReason}
            />
          ) : (
            <AuditGrid
              key={gridKey}
              apiRef={apiRef}
              rows={rows}
              columns={columns}
              view={view}
              loading={loading}
              emptyReason={emptyReason}
              storedState={gridState.storedState}
              onStateChange={gridState.saveGridState}
              onExpansionChange={setExpandedDayIds}
              onShowDetail={setDetailTrip}
              loadMore={loadMoreProps}
              showHeaderFilters={settings.showHeaderFilters}
              onFilterCountChange={count => setFilterCount({ gridKey, count })}
              selection={editable ? { model: selectionModel, onChange: setSelectionModel, isRowSelectable } : undefined}
              topBar={
                editable && selectedTrips.length > 0 ? (
                  <Suspense fallback={null}>
                    <AssignBar
                      trips={selectedTrips}
                      days={editDays}
                      saving={save.isPending}
                      onSave={handleAssign}
                      onClear={() => setSelectionModel([])}
                    />
                  </Suspense>
                ) : undefined
              }
            />
          )}
        </Box>
        {mapSelection && (
          <Suspense fallback={null}>
            <TripMapDrawer
              selection={mapSelection}
              onClose={() => setMapSelection(null)}
              tileId={settings.tileId}
              onTileChange={settings.setTileId}
              storedWidth={settings.mapWidth}
              onWidthChange={settings.setMapWidth}
              distanceDecimals={settings.distanceDecimals}
            />
          </Suspense>
        )}
        {helpOpen && (
          <Suspense fallback={null}>
            <HelpDrawer onClose={() => setHelpOpen(false)} />
          </Suspense>
        )}
        {settingsAnchor && (
          <Suspense fallback={null}>
            <SettingsPopover anchor={settingsAnchor} onClose={() => setSettingsAnchor(null)} settings={settings} isAdmin={isAdmin} />
          </Suspense>
        )}
        {detailTrip && (
          <Suspense fallback={null}>
            <TripDetailDialog trip={detailTrip} onClose={() => setDetailTrip(null)} distanceDecimals={settings.distanceDecimals} driverLabel={driverLabel} />
          </Suspense>
        )}
      </Box>
    </ThemeProvider>
  );
}
